<template>
  <div class="wysiwyg-editor">
      <div v-if="!externalTooltip && !alwaysShowToolbar" :id="toolbarContainerId" class="formatting-panel" ref="formattingPanel" style="display: none;"></div>
      <div class="froala-input" :class="{compress: compress}">
        <div class="editor inline-editor" v-bind="$attrs">
          <froala :tag="tag" ref="editor" v-model.sync="localValue" :id="id" :config="config" class="inline-editor"></froala>
        </div>
        <div v-if="!externalTooltip && alwaysShowToolbar" :id="toolbarContainerId" class="formatting-panel-bottom inline-formatting-panel"></div>
        <context-menu ref="contextMenu"
                      :state="modalStateFor(vueModalID.dynamicContentPopup)"
                      :index="vueModalID.dynamicContentPopup"
                      fixed
                      @close="closeDynamicContentPopup">
          <component v-for="(options, index) in dynamicOptions" :is="options.is" @select="insertDynamicReference"
                     :key="`dynamic-option-${options.title}-${index}`"
                     v-bind="options.props">
            <template #heading>
              <h4 class="popup-title">{{options.title}}</h4>
            </template>
          </component>
        </context-menu>
      </div>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import DynamicFieldsTree from "../dynamicFieldsTrees/dynamicFieldsTree.vue";
  import DeterministicDynamicFieldsTree from "../dynamicFieldsTrees/deterministic-dynamic-fields-tree.vue";
  import ContextMenu from "../contextMenu.vue"
  import {IDynamicReferencedThing} from "../../interfaces/dynamicFieldsInterfaces";
  import localValueEmissionMixin from "../../mixins/localValueEmissionMixin";
  import {StringHelper} from "../../helpers/stringHelper";
  import {IDynamicFieldsOptions} from "../../interfaces/dynamicFieldsInterfaces/dynamicOptionsInterfaces";
  import CertificateVariablePicker from "../../../formability/certificateBuilder/components/layout/certificateVariablePicker.vue";
  import DynamicFieldMixin from "../../../common/mixins/dynamicFieldMixin";
  import {ObjectHelper} from "~/helpers/objectHelper";
  import FroalaEditor from "froala-editor/js/froala_editor.min.js";
  import { buildVarTag } from '~/helpers/froalaHelper';

  export default Vue.extend({
    inheritAttrs: false,
    components: {
      DynamicFieldsTree,
      DeterministicDynamicFieldsTree,
      CertificateVariablePicker,
      ContextMenu
    },
    mixins: [
      localValueEmissionMixin,
      DynamicFieldMixin
    ],
    props: {
      externalTooltip:{
        type: Boolean,
        default: false,
      },
      wrapEditor: {
        type: Boolean,
        default: true
      },
      minHeight: {
        type: Number,
        default: 14,
      },
      maxHeight: {
        type: Number,
        default: 150
      },
      froalaClass: {
        type: String,
        default: ""
      },
      id: {
        type: String,
        required: true
      },
      placeholder: {
        type: String,
        default: 'Type something'
      },
      hidePlaceHolderOnFocus: {
        type: Boolean,
        default: false
      },
      tag: {
        type: String,
        default: "textarea"
      },
      compress: {
        type: Boolean,
        default: false
      },
      dynamicOptions: {
        type: Array as () => Array<IDynamicFieldsOptions>,
        default: () => []
      },
      configOptions: {
        type: Object,
        validator: function (value) {
          //If you have an insertImage button ensure its configured
          if (value.toolbarButtons && value.toolbarButtons.includes("insertImage")) {
            return value.imageUpload
          }
          // If the wysiwyg has an upload image button an upload url and param must be given
          if (value.imageUpload) {
            return value.imageUploadURL && value.imageUploadParam;
          } else {
            return true
          }
        },
      },
      alwaysShowToolbar: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      /**
       * To generally bind anything to the "application" DOM element that acts as a textarea DOM element
       */
      textareaBindings: {
        type: Object,
        default: () => ({}),
      },
    },
    data: function () {
      return {
        froala: {},
        classHook: 'div.fr-element.fr-view',
        froalaInput: null,
        editor: null,
      }
    },
    computed: {
      idFor$(): string {
        return escapeFor$(this.id)
      },
      toolbarContainerId(): string {
        if (this.configOptions && (this.configOptions.toolbarContainer === null || this.configOptions.toolbarContainer === undefined)) {
          return `formatting-panel-${this.id}`;
        }
        return this.externalTooltip ? `${this.configOptions.toolbarContainer}`:  `${this.configOptions.toolbarContainer}-${this.id}`;
      },
      toolbarContainerIdFor$(): string {
        return escapeFor$(this.toolbarContainerId)
      },
      toolbarContainerSelector(): string {
        return `#${this.toolbarContainerIdFor$}`;
      },
      $toolbarContainer() {
        const $parent = this.externalTooltip ? $(document) : $(this.$el)
        return $parent.find(this.toolbarContainerSelector);
      },
      $toolbar() {
        return this.$toolbarContainer.find(`.fr-toolbar`);
      },
      config() {
        // use ObjectHelper.merge instead of Object.assign so that nested objects are recursively merged
        const config = ObjectHelper.merge({}, this.defaultConfig, this.configOptions);
        config.toolbarContainer = this.toolbarContainerSelector;
        return config;
      },
      defaultConfig() {
        const vueComponent = this;
        return {
          //Needs to always be the same key for froala license
          key: 'XAG4eF3C2A7C10A6E6A2A-9UJHAEFZMUJOYGYQEa1c1ZJg1RAeF5C4C3A3E2C2B5D6E4E3==',
          toolbarButtons: [],
          requestHeaders: {
            'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content')
          },
          imageUploadMethod: "POST",
          charCounterCount: false,
          imageUpload: false,
          imageUploadURL: null,
          linkAlwaysBlank: true,
          requestWithCredentials: true,
          toolbarContainer:'#formatting-panel',
          editorClass: 'skip-drag',
          toolbarBottom: true,
          events: {
            initialized: function() {
                let editor = vueComponent.editor = this;
                const element = $(`#${vueComponent.id}`)
                if (element.length > 0) {
                  (<any>$(`#${vueComponent.id}`)[0]).editor = editor;
                }
                // trigger focus event when clicking on image before text
                let toolbar = vueComponent.$toolbar;
                $(editor.$el).on('click', 'img', function () {
                    if(!toolbar.is(":visible")){
                        if($(`.fr-toolbar`).length > 1){
                          $(`.fr-toolbar`).not(toolbar).hide();
                        }
                        editor.events.trigger('focus', [e, editor], true);
                        toolbar.find('button').addClass('fr-disabled')
                    }
                });
                vueComponent.setTextareaBindings()
                if (vueComponent.isDisabled) vueComponent.editor.off()

                vueComponent.$nextTick(() => {
                  vueComponent.$emit('after-initialize')
                })
            },
            click: function(e) {
              vueComponent.dynamicFieldOnClick(e)
            },
            keydown: function(e) {
              vueComponent.dynamicFieldOnKeydown(e)
            },
            focus: function() {
              let editor = this
              if (!vueComponent.alwaysShowToolbar) {
                vueComponent.$toolbar.stop();
                vueComponent.$toolbar.hide();
                vueComponent.$toolbarContainer.show();
                vueComponent.$toolbar.slideDown(200);
              }
              if (vueComponent.hidePlaceHolderOnFocus) {
                editor.opts.placeholderText = "";
              }
              editor.placeholder.refresh();
              editor.size.refresh();
            },
            mouseup: function() {
              let editor = this
              setTimeout(() => {
                editor.size.refresh();
              }, 0);
            },
            blur: function(e) {
              let editor = this
              if (!vueComponent.alwaysShowToolbar) {
                vueComponent.$toolbar.stop();
                vueComponent.$toolbar.slideUp(() => {
                  $(vueComponent.$el).find(vueComponent.toolbarContainerSelector).hide();
                });
              }
              if (vueComponent.hidePlaceHolderOnFocus) {
                editor.opts.placeholderText = vueComponent.placeholder;
              }
              editor.placeholder.refresh();
              editor.size.refresh();
              vueComponent.selectedDynamicField = null
              vueComponent.emitBlur(e)
            },
            'paste.afterCleanup': function(clipboardHtml) {
              // changes pasted links so they open in new tabs
              let snippet = document.createElement('div');
              snippet.innerHTML = clipboardHtml;
              let links = snippet.querySelectorAll('a');
              links.forEach(link => {
                link.target = '_blank';
              });
              return snippet.innerHTML;
            },
          },
          placeholderText: this.placeholder,
          heightMin: this.minHeight,
          heightMax: this.maxHeight,
          tableStyles: {
            'fr-no-borders': 'No Borders',
            'fr-dashed-borders': 'Dashed Borders',
            'fr-alternate-rows': 'Alternate Rows',
          },
          tableInsertHelperOffset: 10
        }
      }
    },
    methods: {
      insertDynamicReference(data: IDynamicReferencedThing): void {
        if (this.config.insertDynamicReferenceTransform) {
          data = this.config.insertDynamicReferenceTransform(data)
        }

        const varTag = buildVarTag(data);

        this.withFroala(froala => {
          this.froala.html.insert(varTag);
          froala.events.focus();
          this.$emit('updateCertificateElementHtml', froala.html.get());
        });
      },
      withFroala(callback) {
        this.froala.selection.restore();
        callback(this.froala);
        this.closeDynamicContentPopup(event);
      },
      openDynamicContentPopup(data): void {
        this.showModal(this.vueModalID.dynamicContentPopup);
        this.$refs.contextMenu.open(data.event);
        this.froala = data.froala;
        this.froala.selection.save();
      },
      closeDynamicContentPopup(event): void {
        this.hideModal(this.vueModalID.dynamicContentPopup);
        this.froala = {};
      },
      initCustomButtons(): void {
        const buttons = this.dynamicOptions.map(o => o.button);
        if (buttons.length > 0) {
          let config = this.config;
          if (config.toolbarButtons.length > 0) {
            config.toolbarButtons.push('|');
          }
          for (let customButton of buttons) {
            const buttonName = customButton.label;
            const buttonCamel = StringHelper.toCamel(buttonName);
            const dash = StringHelper.toDash(buttonName)
            FroalaEditor.RegisterCommand(buttonCamel, {
              title: buttonName,
              icon: `<span class="fa fa-${dash}"></span>`,
              focus: false,
              undo: false,
              refreshAfterCallback: false,
              callback: function (a, b, c) {
                // This callback is registered globally, so if there are multiple <wysiwyg>s on the page, only the
                // callback for the last one created will ever run, so we cannot rely on `this` coming from the scope of
                // `initCustomButton` to be accurate. If all of the visible <wysiwyg>'s have the same props, this problem
                // will only manifest itself as the incorrect one appearing highlighted while the dynamic field menu is
                // open, since `this` is the correct Froala instance. But if they have different dynamic configurations,
                // only the one from the last <wysiwyg> will be used. To correct this, we need to get the Vue component
                // instance for the actual wysiwyg that is being used. We grab the Froala instance (`this` in the callback),
                // get its HTML element (a div.fr-element.fr-view) and find the ancestor of that element with the
                // `wysiwyg-editor` class. That element will be the actual underlying element for the Vue component whose
                // Froala instance had a button activated. The `__vue__` property on the HTML element then gives us the
                // actual Vue component instance which we can call the `openDynamicContentPopup` method on. (`__vue__`
                // is an internal Vue property, and it would be nice not to depend on it, but there doesn't seem to be
                // any other way of getting the component instance attached to an arbitrary HTML element.)
                const $froalaEl = $(this.el);
                const $componentEl = $froalaEl.parents('.wysiwyg-editor');
                const vueComponent = $componentEl.get(0).__vue__;
                const position = $froalaEl.position();
                // manually create click event - can choose to trigger it in parent or use it's client position info
                const event = new MouseEvent('click', {
                  view: window,
                  bubbles: true,
                  cancelable: true,
                  clientX: position.left,
                  clientY: position.top,
                  relatedTarget: $froalaEl.get(0)
                });
                vueComponent.openDynamicContentPopup({event: event, froala: this});
              }
            });
            config.toolbarButtons.push(buttonCamel);
            if (customButton.shortcut) {
              FroalaEditor.RegisterShortcut(customButton.shortcut.keyCode, buttonCamel, null, customButton.shortcut.letter);
            }
          }
        }
      },
      handleDisabled(disabled: boolean) {
        if (this.editor) {
          disabled ? this.editor.edit.off() : this.editor.edit.on()
        }
      },
      emitBlur(event) {
        this.$emit('blur', event);
      },
      setTextareaBindings() {
        this.$nextTick(() => {
          const $box = $(this.$el).find('.fr-box')
          if (this.textareaBindings['aria-required'] !== undefined) {
            $box.attr('aria-required', this.textareaBindings['aria-required'])
          } else {
            $box.removeAttr('aria-required')
          }

          if (this.textareaBindings['aria-invalid'] !== undefined) {
            $box.attr('aria-invalid', this.textareaBindings['aria-invalid'])
          } else {
            $box.removeAttr('aria-invalid')
          }

          if (this.textareaBindings['aria-describedby'] !== undefined) {
            $box.attr('aria-describedby', this.textareaBindings['aria-describedby'])
          } else {
            $box.removeAttr('aria-describedby')
          }
        })
      },
    },
    created() {
      this.initCustomButtons();
    },
    mounted() {
      this.$emit("mounted");
      if (this.id) {
        $(`label[for="${this.idFor$}"]`).on('click.froala', () => {
          this.editor.events.focus()
        });
      }
      this.setTextareaBindings()
    },
    beforeDestroy() {
      $(`label[for="${this.id}"]`).off('click.froala');
      $('body').off('click', this.dynamicFieldOnClick);
      $('body').off('keydown', this.dynamicFieldOnKeydown);
    },
    watch: {
      value(newVal) {
        if (newVal === null || this.localValue === undefined) {
          this.localValue = "";
        }
      },
      disabled(newVal) {
        this.handleDisabled(newVal)
      },
      editor(newVal) {
        if (newVal) {
          this.handleDisabled(this.disabled)
        }
      },
      textareaBindings(newVal) {
        this.setTextareaBindings()
      },
    }
  });

  /**
   * escape meta char for jquery find
   * @see {link: https://api.jquery.com/category/selectors/}
   */
  function escapeFor$(text: string): string {
    return text.replace(/\./g, '\\.')
  }
</script>
